(() => {
  const selector = '[data-e-live-search]';
  const cache = new Map();

  const debounce = (callback, delay) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), delay);
    };
  };

  const init = (root) => {
    if (!root || root.dataset.eLiveSearchReady === 'true') return;

    const form = root.querySelector('[data-e-live-search-form]');
    const input = root.querySelector('[data-e-live-search-input]');
    const clearButton = root.querySelector('[data-e-live-search-clear]');
    const results = root.querySelector('[data-e-live-search-results]');

    if (!form || !input || !clearButton || !results) return;

    const predictiveUrl = (root.dataset.predictiveUrl || '/search/suggest').replace(/\.json(?:\?.*)?$/, '');
    const searchUrl = root.dataset.searchUrl || '/search';
    const currency = root.dataset.currency || window.Shopify?.currency?.active || 'MAD';
    let controller = null;
    let formatter = null;

    const money = (value) => {
      const amount = Number.parseFloat(String(value ?? '').replace(',', '.'));
      if (!Number.isFinite(amount)) return '';

      try {
        formatter ||= new Intl.NumberFormat(document.documentElement.lang || 'fr-MA', {
          style: 'currency',
          currency,
          currencyDisplay: 'code',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        });
        return formatter.format(amount);
      } catch (_error) {
        return `${amount.toFixed(2)} ${currency}`;
      }
    };

    const close = () => {
      if (results.hidden) return;
      root.classList.remove('is-open');
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    };

    const open = () => {
      if (!results.hidden) return;
      root.classList.add('is-open');
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const setClearButton = () => {
      clearButton.hidden = input.value.length === 0;
    };

    const createPrice = (product) => {
      const price = Number.parseFloat(String(product.price_min ?? product.price ?? '').replace(',', '.'));
      const compare = Number.parseFloat(String(product.compare_at_price_min ?? product.compare_at_price ?? '').replace(',', '.'));
      const wrapper = document.createElement('div');
      wrapper.className = 'e-header-live-search__price';

      const current = document.createElement('span');
      current.className = 'e-header-live-search__price-current';
      current.textContent = money(price);
      wrapper.append(current);

      if (Number.isFinite(compare) && compare > price) {
        const old = document.createElement('s');
        old.className = 'e-header-live-search__price-old';
        old.textContent = money(compare);
        wrapper.append(old);
      }

      return wrapper;
    };

    const createProduct = (product) => {
      const item = document.createElement('li');
      item.className = 'e-header-live-search__item';
      item.setAttribute('role', 'option');

      const link = document.createElement('a');
      link.className = 'e-header-live-search__product';
      link.href = product.url || '#';

      const media = document.createElement('span');
      media.className = 'e-header-live-search__media';
      const imageValue = product.image || product.featured_image;
      const imageUrl = typeof imageValue === 'string' ? imageValue : imageValue?.url || imageValue?.src;

      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = product.title || '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = 88;
        image.height = 88;
        media.append(image);
      } else {
        media.classList.add('e-header-live-search__media--empty');
      }

      const content = document.createElement('span');
      content.className = 'e-header-live-search__content';

      if (product.vendor) {
        const vendor = document.createElement('span');
        vendor.className = 'e-header-live-search__vendor';
        vendor.textContent = product.vendor;
        content.append(vendor);
      }

      const title = document.createElement('span');
      title.className = 'e-header-live-search__title';
      title.textContent = product.title || '';
      content.append(title, createPrice(product));
      link.append(media, content);
      item.append(link);
      return item;
    };

    const render = (products, query) => {
      const fragment = document.createDocumentFragment();

      if (!products.length) {
        const empty = document.createElement('p');
        empty.className = 'e-header-live-search__empty';
        empty.textContent = `Aucun produit trouvé pour « ${query} ».`;
        fragment.append(empty);
      } else {
        const list = document.createElement('ul');
        list.className = 'e-header-live-search__grid';
        list.setAttribute('role', 'listbox');
        products.slice(0, 6).forEach((product) => list.append(createProduct(product)));

        const footer = document.createElement('a');
        footer.className = 'e-header-live-search__all';
        footer.href = `${searchUrl}?options%5Bprefix%5D=last&q=${encodeURIComponent(query)}`;
        footer.textContent = `Voir tous les résultats pour « ${query} »`;
        fragment.append(list, footer);
      }

      results.replaceChildren(fragment);
      open();
    };

    const search = async () => {
      const query = input.value.trim();
      const key = query.toLocaleLowerCase();

      if (query.length < 2) {
        controller?.abort();
        results.replaceChildren();
        close();
        return;
      }

      if (cache.has(key)) {
        render(cache.get(key), query);
        return;
      }

      controller?.abort();
      controller = new AbortController();

      const loading = document.createElement('p');
      loading.className = 'e-header-live-search__loading';
      loading.textContent = 'Recherche en cours…';
      results.replaceChildren(loading);
      open();

      const parameters = new URLSearchParams();
      parameters.set('q', query);
      parameters.set('resources[type]', 'product');
      parameters.set('resources[limit]', '6');
      parameters.set('resources[options][unavailable_products]', 'hide');

      try {
        const response = await fetch(`${predictiveUrl}.json?${parameters.toString()}`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });

        if (!response.ok) throw new Error(`Predictive search: ${response.status}`);
        const payload = await response.json();
        if (input.value.trim() !== query) return;

        const products = payload?.resources?.results?.products || [];
        cache.set(key, products);
        render(products, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        results.replaceChildren();
        const fallback = document.createElement('a');
        fallback.className = 'e-header-live-search__all e-header-live-search__all--fallback';
        fallback.href = `${searchUrl}?options%5Bprefix%5D=last&q=${encodeURIComponent(query)}`;
        fallback.textContent = 'Afficher les résultats de recherche';
        results.append(fallback);
        open();
      }
    };

    const debouncedSearch = debounce(search, 240);

    input.addEventListener('input', () => {
      setClearButton();
      debouncedSearch();
    });

    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2 && results.childElementCount) open();
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close();
        input.focus();
      } else if (event.key === 'ArrowDown' && !results.hidden) {
        const firstLink = results.querySelector('a');
        if (firstLink) {
          event.preventDefault();
          firstLink.focus();
        }
      }
    });

    clearButton.addEventListener('click', () => {
      controller?.abort();
      input.value = '';
      setClearButton();
      results.replaceChildren();
      close();
      input.focus();
    });

    /* The global handler is now a no-op while results are closed. */
    document.addEventListener('pointerdown', (event) => {
      if (results.hidden || root.contains(event.target)) return;
      close();
    }, { passive: true });

    form.addEventListener('submit', (event) => {
      if (!input.value.trim()) event.preventDefault();
    });

    root.dataset.eLiveSearchReady = 'true';
    setClearButton();
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(selector)) init(scope);
    scope.querySelectorAll?.(selector).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
